import { Injectable } from "@angular/core";
import { HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { OAuthService } from "angular-oauth2-oidc";
import { environment } from "../../../environments/environment";

export enum AltinnAttachmentUploadStatus {
  InProgress = 0,
  Completed = 1,
  Failed = 2,
  WaitingForAntiVirus = 3,
}

export enum AltinnSubmissionStep {
  AttachmentUpload = 0,
  Finalize = 1,
}

// Wire shape pushed by the backend inside a progress message.
export interface IAltinnFinalizeResult {
  success: boolean;
  instanceId: string | null;
  errorMessage: string | null;
}

// `connectionLost` means we never learned the real outcome: the backend may well have
// completed the submission in Altinn. Callers must not offer a plain retry in that case,
// because preparing a submission again creates a second Altinn instance.
export interface IAltinnUploadOutcome {
  success: boolean;
  connectionLost: boolean;
}

export interface IAltinnFinalizeOutcome extends IAltinnFinalizeResult {
  connectionLost: boolean;
}

interface IAltinnAttachmentUploadProgress {
  altinnSubmissionId: string;
  step: AltinnSubmissionStep;
  attachmentId: string;
  status: AltinnAttachmentUploadStatus;
  errorMessage: string | null;
  finalize: IAltinnFinalizeResult | null;
}

interface IWaiter<T> {
  resolve: (value: T) => void;
  timeoutHandle: ReturnType<typeof setTimeout>;
  stallHandle: ReturnType<typeof setTimeout> | null;
  reconcile?: () => Promise<T>;
}

const FINALIZE_TIMEOUT_MS = 30 * 60 * 1000;
const RECONNECT_DELAY_CAP_MS = 60 * 1000;
// Reconnecting must be bounded, otherwise onclose never fires and a pending waiter sits
// until its 30-minute timeout while the connection retries forever in the background.
const RECONNECT_GIVE_UP_MS = 5 * 60 * 1000;
// An attachment upload never has a legitimate reason to run longer than this (no antivirus-scan
// retry loop applies to uploads, only to finalize). Armed unconditionally from the moment the
// waiter is created, not just after our own connection reconnects: the completion message can
// also be lost on the server-to-server relay between the main backend and this app while our
// browser's own connection stays healthy throughout, which onreconnected would never observe.
const ATTACHMENT_UPLOAD_STALL_MS = 3 * 60 * 1000;
// The finalize waiter gets a shorter grace period than a plain stall timeout, because once
// it elapses we reconcile against the real application status instead of just giving up.
const FINALIZE_RECONCILE_DELAY_MS = 2 * 60 * 1000;
const INITIAL_CONNECT_ATTEMPTS = 3;
const INITIAL_CONNECT_RETRY_DELAY_MS = 2000;

export const CONNECTION_LOST_MESSAGE = "Mistet kontakten med serveren under sending";

@Injectable({ providedIn: "root" })
export class AltinnSubmissionProgressHubService {
  private hubConnection: HubConnection | null = null;
  private readonly attachmentWaiters = new Map<string, IWaiter<IAltinnUploadOutcome>>();
  private readonly finalizeWaiters = new Map<string, IWaiter<IAltinnFinalizeOutcome>>();
  private readonly antiVirusListeners = new Map<string, () => void>();
  private connectedSubmissionId: string | null = null;
  private connectionInterruptedListener: ((interrupted: boolean) => void) | null = null;

  constructor(private readonly oauthService: OAuthService) {}

  // Build one hub connection per submission. The submissionId is sent as a query
  // parameter so the server-side OnConnectedAsync joins the right group — joining itself
  // needs no client invoke(), which avoids the "Cannot send data when the connection is
  // not in the 'Connected' state" race when called during automatic reconnect, and means
  // a reconnect re-joins the group automatically. A separate ConfirmJoinedAsync invoke
  // below confirms that join has landed before we rely on it (see its call site).
  async connectForSubmissionAsync(altinnSubmissionId: string): Promise<void> {
    if (this.connectedSubmissionId === altinnSubmissionId && this.hubConnection?.state === HubConnectionState.Connected) {
      return;
    }

    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = null;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${environment.apiUrl}/hubs/altinnSubmissionProgress?submissionId=${encodeURIComponent(altinnSubmissionId)}`, {
        accessTokenFactory: () => this.oauthService.getAccessToken(),
      })
      .withKeepAliveInterval(80000)
      .withServerTimeout(240000)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) =>
          retryContext.elapsedMilliseconds >= RECONNECT_GIVE_UP_MS
            ? null
            : Math.min(10000 * Math.pow(2, retryContext.previousRetryCount), RECONNECT_DELAY_CAP_MS),
      })
      .build();
    this.hubConnection = connection;

    connection.on("submissionProgress", (progress: IAltinnAttachmentUploadProgress) => {
      this.handleProgress(progress);
    });

    // A dropped socket does not stop the backend from finishing the submission, so a
    // reconnect must not be reported as a failure. Waiters stay pending and are given a
    // bounded grace period once the connection is back - armed only once the group join
    // is confirmed, since a reconnect re-runs the exact same join-vs-broadcast race as the
    // initial connect (see the invoke call below).
    connection.onreconnecting(() => this.connectionInterruptedListener?.(true));
    connection.onreconnected(() => {
      this.connectionInterruptedListener?.(false);
      void this.retryAsync(() => connection.invoke("ConfirmJoinedAsync")).finally(() => this.armStallTimers());
    });
    connection.onclose(() => this.failAllWaiters(true));

    // withAutomaticReconnect does not cover the initial connect.
    await this.retryAsync(() => connection.start());

    // The server only guarantees group membership (see OnConnectedAsync) once this
    // invoke's response comes back - start() resolving is not enough, since the client
    // handshake ack can arrive before OnConnectedAsync has finished running. Without this,
    // backend work that completes fast enough can broadcast to the group before the join
    // has landed, and the browser waits out the full timeout instead of hearing the result.
    await this.retryAsync(() => connection.invoke("ConfirmJoinedAsync"));

    this.connectedSubmissionId = altinnSubmissionId;
  }

  waitForUploadAsync(attachmentId: string): Promise<IAltinnUploadOutcome> {
    return new Promise<IAltinnUploadOutcome>((resolve) => {
      // This is the only deadline for an attachment waiter - see ATTACHMENT_UPLOAD_STALL_MS.
      const timeoutHandle = setTimeout(
        () => this.settle(this.attachmentWaiters, attachmentId, { success: false, connectionLost: true }),
        ATTACHMENT_UPLOAD_STALL_MS,
      );
      const stallHandle = setTimeout(
        () => this.settle(this.attachmentWaiters, attachmentId, { success: false, connectionLost: true }),
        ATTACHMENT_UPLOAD_STALL_MS,
      );

      this.attachmentWaiters.set(attachmentId, { resolve, timeoutHandle, stallHandle });
    });
  }

  waitForFinalizeAsync(
    altinnSubmissionId: string,
    reconcile?: () => Promise<IAltinnFinalizeOutcome>,
  ): Promise<IAltinnFinalizeOutcome> {
    return new Promise<IAltinnFinalizeOutcome>((resolve) => {
      const timeoutHandle = setTimeout(
        () => this.settle(this.finalizeWaiters, altinnSubmissionId, {
          success: false,
          instanceId: null,
          errorMessage: "Finalize timed out waiting for backend response",
          connectionLost: false,
        }),
        FINALIZE_TIMEOUT_MS,
      );

      this.finalizeWaiters.set(altinnSubmissionId, { resolve, timeoutHandle, stallHandle: null, reconcile });
    });
  }

  onWaitingForAntiVirus(altinnSubmissionId: string, callback: () => void): void {
    this.antiVirusListeners.set(altinnSubmissionId, callback);
  }

  clearWaitingForAntiVirus(altinnSubmissionId: string): void {
    this.antiVirusListeners.delete(altinnSubmissionId);
  }

  // Lets the caller tell the user the connection is being re-established instead of
  // leaving the loading mask on a message that is no longer true.
  onConnectionInterruptedChange(callback: ((interrupted: boolean) => void) | null): void {
    this.connectionInterruptedListener = callback;
  }

  async disconnectAsync(): Promise<void> {
    this.failAllWaiters(false);
    this.connectedSubmissionId = null;
    this.connectionInterruptedListener = null;

    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = null;
    }
  }

  // Shared by the initial connect() and the post-connect ConfirmJoinedAsync invoke, so a
  // transient failure right after connecting gets the same retry treatment as the connect
  // itself instead of failing the whole submission attempt outright.
  private async retryAsync<T>(operation: () => Promise<T>): Promise<T> {
    for (let attempt = 1; ; attempt++) {
      try {
        return await operation();
      } catch (error) {
        if (attempt >= INITIAL_CONNECT_ATTEMPTS) {
          throw error;
        }
        await new Promise<void>(resolve => setTimeout(resolve, INITIAL_CONNECT_RETRY_DELAY_MS * attempt));
      }
    }
  }

  private settle<T>(waiters: Map<string, IWaiter<T>>, key: string, value: T): void {
    const waiter = waiters.get(key);
    if (!waiter) {
      return;
    }

    waiters.delete(key);
    clearTimeout(waiter.timeoutHandle);
    if (waiter.stallHandle !== null) {
      clearTimeout(waiter.stallHandle);
    }
    waiter.resolve(value);
  }

  private armStallTimers(): void {
    // Attachment waiters already arm their own stall timer unconditionally in
    // waitForUploadAsync, so there is nothing to do for them here on reconnect.
    for (const [submissionId, waiter] of this.finalizeWaiters) {
      if (waiter.stallHandle !== null) {
        continue;
      }
      waiter.stallHandle = setTimeout(
        () => this.resolveStalledFinalize(submissionId, waiter),
        FINALIZE_RECONCILE_DELAY_MS,
      );
    }
  }

  // Called once the finalize waiter has been reconnected but silent for
  // FINALIZE_RECONCILE_DELAY_MS. A reconnect proves the socket is healthy again, so silence at
  // this point most likely means the finalize message was emitted while we were disconnected -
  // check the real application status instead of assuming failure.
  private resolveStalledFinalize(submissionId: string, waiter: IWaiter<IAltinnFinalizeOutcome>): void {
    const fallback: IAltinnFinalizeOutcome = {
      success: false,
      instanceId: null,
      errorMessage: CONNECTION_LOST_MESSAGE,
      connectionLost: true,
    };

    if (!waiter.reconcile) {
      this.settle(this.finalizeWaiters, submissionId, fallback);
      return;
    }

    waiter.reconcile().then(
      (result) => this.settle(this.finalizeWaiters, submissionId, result),
      () => this.settle(this.finalizeWaiters, submissionId, fallback),
    );
  }

  private failAllWaiters(connectionLost: boolean): void {
    for (const attachmentId of [...this.attachmentWaiters.keys()]) {
      this.settle(this.attachmentWaiters, attachmentId, { success: false, connectionLost });
    }

    for (const submissionId of [...this.finalizeWaiters.keys()]) {
      this.settle(this.finalizeWaiters, submissionId, {
        success: false,
        instanceId: null,
        errorMessage: connectionLost ? CONNECTION_LOST_MESSAGE : "Sending av søknad feilet",
        connectionLost,
      });
    }

    this.antiVirusListeners.clear();
  }

  private handleProgress(progress: IAltinnAttachmentUploadProgress): void {
    if (progress.step === AltinnSubmissionStep.Finalize) {
      this.handleFinalizeProgress(progress);
      return;
    }

    if (progress.status === AltinnAttachmentUploadStatus.InProgress) {
      return;
    }

    this.settle(this.attachmentWaiters, progress.attachmentId, {
      success: progress.status === AltinnAttachmentUploadStatus.Completed,
      connectionLost: false,
    });
  }

  private handleFinalizeProgress(progress: IAltinnAttachmentUploadProgress): void {
    if (progress.status === AltinnAttachmentUploadStatus.WaitingForAntiVirus) {
      const listener = this.antiVirusListeners.get(progress.altinnSubmissionId);
      listener?.();
      return;
    }

    if (progress.status === AltinnAttachmentUploadStatus.InProgress) {
      return;
    }

    this.antiVirusListeners.delete(progress.altinnSubmissionId);
    this.settle(this.finalizeWaiters, progress.altinnSubmissionId, {
      ...(progress.finalize ?? {
        success: progress.status === AltinnAttachmentUploadStatus.Completed,
        instanceId: null,
        errorMessage: progress.errorMessage,
      }),
      connectionLost: false,
    });
  }
}
